import { supabase } from '../lib/supabase';
import { PostgrestError } from '@supabase/supabase-js';
import { applyDomainFilterToQuery } from '../hooks/useDomainQuery';

// ============================================================================
// INTERFACES TYPESCRIPT
// ============================================================================

export interface ElearningCategory {
  id: string;
  tenant_id: string;
  name: string;
  slug: string;
  description: string | null;
  icon: string | null;
  color: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ElearningInstructor {
  id: string;
  tenant_id: string;
  user_id: string | null;
  name: string;
  email: string;
  bio: string | null;
  avatar_url: string | null;
  expertise: string[] | null;
  social_links: Record<string, any>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ElearningCourse {
  id: string;
  tenant_id: string;
  title: string;
  slug: string;
  description: string | null;
  content: string | null;
  price: number;
  currency: string;
  thumbnail_url: string | null;
  video_preview_url: string | null;
  difficulty_level: 'beginner' | 'intermediate' | 'advanced';
  duration_hours: number;
  language: string;
  status: 'draft' | 'published' | 'archived';
  category_id: string | null;
  instructor_id: string | null;
  tags: string[] | null;
  requirements: string[] | null;
  learning_objectives: string[] | null;
  sort_order: number;
  is_featured: boolean;
  enrollment_count: number;
  rating_average: number;
  rating_count: number;
  created_at: string;
  updated_at: string;
  category?: ElearningCategory;
  instructor?: ElearningInstructor;
  modules?: ElearningModule[];
}

export interface ElearningModule {
  id: string;
  course_id: string;
  title: string;
  description: string | null;
  sort_order: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
  lessons?: ElearningLesson[];
}

export interface ElearningLesson {
  id: string;
  module_id: string;
  title: string;
  content: string | null;
  content_type: 'video' | 'text' | 'quiz' | 'assignment' | 'file';
  video_url: string | null;
  video_duration: number;
  file_url: string | null;
  file_name: string | null;
  file_size: number | null;
  sort_order: number;
  is_free: boolean;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

export interface ElearningCourseReview {
  id: string;
  tenant_id: string;
  course_id: string;
  student_id: string;
  enrollment_id: string;
  rating: number;
  review_text: string | null;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// FONCTIONS PUBLIQUES (LECTURE)
// ============================================================================

/**
 * Récupère toutes les catégories de cours avec filtres optionnels
 */
export async function getElearningCategories(tenantId: string, domain?: string) {
  try {
    let query = supabase
      .from('elearn_categories')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });
    
    query = applyDomainFilterToQuery(query);
    
    const { data, error } = await query;

    if (error) throw error;

    return data as ElearningCategory[];
  } catch (error) {
    console.error('Erreur lors de la récupération des catégories e-learning:', error);
    throw error;
  }
}

/**
 * Récupère le nombre de cours par catégorie
 */
export async function getCategoriesWithCourseCount(tenantId: string, domain?: string) {
  try {
    let query = supabase
      .from('elearn_categories')
      .select(`
        *,
        course_count:elearn_courses(count)
      `)
      .eq('is_active', true)
      .eq('elearn_courses.status', 'published')
      .order('sort_order', { ascending: true });
    
    query = applyDomainFilterToQuery(query);
    
    const { data, error } = await query;

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Erreur lors de la récupération des catégories avec comptage:', error);
    throw error;
  }
}

/**
 * Récupère tous les cours disponibles avec filtres optionnels
 */
export async function getAvailableCourses({
  tenantId,
  categorySlug = null,
  instructorId = null,
  difficultyLevel = null,
  search = null,
  categoryIds = null,
  instructorIds = null,
  difficultyLevels = null,
  priceMin = null,
  priceMax = null,
  durationMin = null,
  durationMax = null,
  sortBy = 'created_at',
  sortOrder = 'desc',
  limit = 12,
  offset = 0
}: {
  tenantId: string;
  categorySlug?: string | null;
  instructorId?: string | null;
  difficultyLevel?: 'beginner' | 'intermediate' | 'advanced' | null;
  search?: string | null;
  categoryIds?: string[] | null;
  instructorIds?: string[] | null;
  difficultyLevels?: string[] | null;
  priceMin?: number | null;
  priceMax?: number | null;
  durationMin?: number | null;
  durationMax?: number | null;
  sortBy?: 'created_at' | 'price' | 'title' | 'rating_average' | 'enrollment_count';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}) {
  try {
    let query = supabase
      .from('elearn_courses')
      .select(`
        *,
        category:category_id(id, name, slug, icon, color),
        instructor:instructor_id(id, name, bio, avatar_url, expertise)
      `, { count: 'exact' })
      .eq('status', 'published')
      .order(sortBy, { ascending: sortOrder === 'asc' })
      .limit(limit)
      .range(offset, offset + limit - 1);
    
    query = applyDomainFilterToQuery(query);

    // Filtrer par catégorie si spécifié
    if (categorySlug) {
      let categoryQuery = supabase
        .from('elearn_categories')
        .select('id')
        .eq('slug', categorySlug);
      
      categoryQuery = applyDomainFilterToQuery(categoryQuery);
      const { data: category } = await categoryQuery.single();

      if (category) {
        query = query.eq('category_id', category.id);
      }
    }

    // Filtrer par instructeur si spécifié
    if (instructorId) {
      query = query.eq('instructor_id', instructorId);
    }

    // Filtrer par niveau de difficulté si spécifié
    if (difficultyLevel) {
      query = query.eq('difficulty_level', difficultyLevel);
    }
    
    // Filtrer par plusieurs niveaux de difficulté si spécifiés
    if (difficultyLevels && difficultyLevels.length > 0) {
      query = query.in('difficulty_level', difficultyLevels);
    }

    // Filtrer par plusieurs catégories si spécifiées
    if (categoryIds && categoryIds.length > 0) {
      query = query.in('category_id', categoryIds);
    }

    // Filtrer par plusieurs instructeurs si spécifiés
    if (instructorIds && instructorIds.length > 0) {
      query = query.in('instructor_id', instructorIds);
    }

    // Filtrer par prix minimum si spécifié
    if (priceMin !== null) {
      query = query.gte('price', priceMin);
    }

    // Filtrer par prix maximum si spécifié
    if (priceMax !== null) {
      query = query.lte('price', priceMax);
    }

    // Filtrer par durée minimum si spécifiée
    if (durationMin !== null) {
      query = query.gte('duration_hours', durationMin);
    }

    // Filtrer par durée maximum si spécifiée
    if (durationMax !== null) {
      query = query.lte('duration_hours', durationMax);
    }

    // Recherche textuelle si spécifiée
    if (search) {
      query = query.or(`title.ilike.%${search}%,description.ilike.%${search}%,tags.cs.{${search}}`);
    }

    const { data, error, count } = await query;

    if (error) throw error;

    return {
      courses: data as ElearningCourse[],
      count
    };
  } catch (error) {
    console.error('Erreur lors de la récupération des cours:', error);
    throw error;
  }
}

/**
 * Récupère un cours par son slug avec tous ses modules et leçons
 */
export async function getCourseBySlug(tenantId: string, slug: string, domain?: string) {
  try {
    let query = supabase
      .from('elearn_courses')
      .select(`
        *,
        category:category_id(id, name, slug, icon, color),
        instructor:instructor_id(id, name, bio, avatar_url, expertise, social_links),
        modules:elearn_modules(
          *,
          lessons:elearn_lessons(*)
        )
      `)
      .eq('slug', slug)
      .eq('status', 'published');
    
    query = applyDomainFilterToQuery(query);
    const { data, error } = await query.single();

    if (error) throw error;

    // Trier les modules et leçons par ordre
    if (data.modules) {
      data.modules.sort((a: any, b: any) => a.sort_order - b.sort_order);
      data.modules.forEach((module: any) => {
        if (module.lessons) {
          module.lessons.sort((a: any, b: any) => a.sort_order - b.sort_order);
        }
      });
    }

    return data as ElearningCourse;
  } catch (error) {
    console.error(`Erreur lors de la récupération du cours ${slug}:`, error);
    throw error;
  }
}

/**
 * Récupère les cours en vedette
 */
export async function getFeaturedCourses(tenantId: string, limit: number = 6, domain?: string) {
  try {
    let query = supabase
      .from('elearn_courses')
      .select(`
        *,
        category:category_id(id, name, slug, icon, color),
        instructor:instructor_id(id, name, bio, avatar_url)
      `)
      .eq('status', 'published')
      .eq('is_featured', true)
      .order('sort_order', { ascending: true })
      .limit(limit);
    
    query = applyDomainFilterToQuery(query);
    const { data, error } = await query;

    if (error) throw error;

    return data as ElearningCourse[];
  } catch (error) {
    console.error('Erreur lors de la récupération des cours en vedette:', error);
    throw error;
  }
}

/**
 * Récupère les cours les plus populaires
 */
export async function getPopularCourses(tenantId: string, limit: number = 6, domain?: string) {
  try {
    let query = supabase
      .from('elearn_courses')
      .select(`
        *,
        category:category_id(id, name, slug, icon, color),
        instructor:instructor_id(id, name, bio, avatar_url)
      `)
      .eq('status', 'published')
      .order('enrollment_count', { ascending: false })
      .limit(limit);
    
    query = applyDomainFilterToQuery(query);
    const { data, error } = await query;

    if (error) throw error;

    return data as ElearningCourse[];
  } catch (error) {
    console.error('Erreur lors de la récupération des cours populaires:', error);
    throw error;
  }
}

/**
 * Récupère les cours les mieux notés
 */
export async function getTopRatedCourses(tenantId: string, limit: number = 6, domain?: string) {
  try {
    let query = supabase
      .from('elearn_courses')
      .select(`
        *,
        category:category_id(id, name, slug, icon, color),
        instructor:instructor_id(id, name, bio, avatar_url)
      `)
      .eq('status', 'published')
      .gte('rating_count', 5) // Au moins 5 évaluations
      .order('rating_average', { ascending: false })
      .limit(limit);
    
    query = applyDomainFilterToQuery(query);
    const { data, error } = await query;

    if (error) throw error;

    return data as ElearningCourse[];
  } catch (error) {
    console.error('Erreur lors de la récupération des cours les mieux notés:', error);
    throw error;
  }
}

/**
 * Récupère tous les instructeurs actifs
 */
export async function getInstructors(tenantId: string, domain?: string) {
  try {
    let query = supabase
      .from('elearn_instructors')
      .select(`
        *,
        course_count:elearn_courses(count)
      `)
      .eq('is_active', true)
      .order('name', { ascending: true });
    
    query = applyDomainFilterToQuery(query);
    const { data, error } = await query;

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Erreur lors de la récupération des instructeurs:', error);
    throw error;
  }
}

/**
 * Récupère un instructeur par son ID avec ses cours
 */
export async function getInstructorById(tenantId: string, instructorId: string, domain?: string) {
  try {
    let query = supabase
      .from('elearn_instructors')
      .select(`
        *,
        courses:elearn_courses(
          id, title, slug, description, price, thumbnail_url, 
          difficulty_level, duration_hours, enrollment_count, 
          rating_average, rating_count, created_at
        )
      `)
      .eq('id', instructorId)
      .eq('is_active', true);
    
    query = applyDomainFilterToQuery(query);
    const { data, error } = await query.single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error(`Erreur lors de la récupération de l'instructeur ${instructorId}:`, error);
    throw error;
  }
}

/**
 * Récupère les avis d'un cours
 */
export async function getCourseReviews(tenantId: string, courseId: string, limit: number = 10, offset: number = 0) {
  try {
    let query = supabase
      .from('elearn_course_reviews')
      .select(`
        *,
        student:student_id(id, email, raw_user_meta_data)
      `)
      .eq('course_id', courseId)
      .eq('is_published', true)
      .order('created_at', { ascending: false })
      .limit(limit)
      .range(offset, offset + limit - 1);
    
    query = applyDomainFilterToQuery(query);
    const { data, error } = await query;

    if (error) throw error;

    return data;
  } catch (error) {
    console.error(`Erreur lors de la récupération des avis du cours ${courseId}:`, error);
    throw error;
  }
}

/**
 * Vérifie si un utilisateur est inscrit à un cours
 */
export async function isUserEnrolledInCourse(tenantId: string, userId: string, courseId: string) {
  try {
    let query = supabase
      .from('elearn_enrollments')
      .select('id, status')
      .eq('student_id', userId)
      .eq('course_id', courseId);
    
    query = applyDomainFilterToQuery(query);
    const { data, error } = await query.single();

    if (error && error.code !== 'PGRST116') { // PGRST116 = pas de résultat trouvé
      throw error;
    }

    return data ? { isEnrolled: true, status: data.status } : { isEnrolled: false, status: null };
  } catch (error) {
    console.error(`Erreur lors de la vérification d'inscription:`, error);
    throw error;
  }
}

/**
 * Récupère les statistiques générales des cours
 */
export async function getCoursesStats(tenantId: string) {
  try {
    let coursesQuery = supabase
      .from('elearn_courses')
      .select('id', { count: 'exact' })
      .eq('status', 'published');
    
    coursesQuery = applyDomainFilterToQuery(coursesQuery);
    const { data: totalCourses, error: coursesError } = await coursesQuery;

    if (coursesError) throw coursesError;

    let enrollmentsQuery = supabase
      .from('elearn_enrollments')
      .select('id', { count: 'exact' })
      .eq('status', 'active');
    
    enrollmentsQuery = applyDomainFilterToQuery(enrollmentsQuery);
    const { data: totalEnrollments, error: enrollmentsError } = await enrollmentsQuery;

    if (enrollmentsError) throw enrollmentsError;

    let instructorsQuery = supabase
      .from('elearn_instructors')
      .select('id', { count: 'exact' })
      .eq('is_active', true);
    
    instructorsQuery = applyDomainFilterToQuery(instructorsQuery);
    const { data: totalInstructors, error: instructorsError } = await instructorsQuery;

    if (instructorsError) throw instructorsError;

    return {
      totalCourses: totalCourses?.length || 0,
      totalEnrollments: totalEnrollments?.length || 0,
      totalInstructors: totalInstructors?.length || 0
    };
  } catch (error) {
    console.error('Erreur lors de la récupération des statistiques:', error);
    throw error;
  }
}

// ============================================================================
// FONCTIONS UTILITAIRES
// ============================================================================

/**
 * Génère un slug unique à partir d'un titre
 */
export function generateCourseSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Supprimer les caractères spéciaux
    .replace(/\s+/g, '-') // Remplacer les espaces par des tirets
    .replace(/-+/g, '-') // Éviter les tirets multiples
    .trim();
}

/**
 * Formate la durée en heures en texte lisible
 */
export function formatCourseDuration(hours: number): string {
  if (hours < 1) {
    return `${Math.round(hours * 60)} min`;
  } else if (hours === 1) {
    return '1 heure';
  } else {
    return `${hours} heures`;
  }
}

/**
 * Formate le niveau de difficulté en français
 */
export function formatDifficultyLevel(level: string): string {
  const levels = {
    beginner: 'Débutant',
    intermediate: 'Intermédiaire',
    advanced: 'Avancé'
  };
  return levels[level as keyof typeof levels] || level;
}

/**
 * Calcule le prix avec remise si applicable
 */
export function calculateDiscountedPrice(originalPrice: number, discountPercentage: number = 0): number {
  if (discountPercentage <= 0) return originalPrice;
  return originalPrice * (1 - discountPercentage / 100);
}
